/* ═══════════════════════════════════════════════════════════════════════════
   Direction C — share copies the link on the first click
   Loaded from sections/main-product.liquid. Hook: `data-dc-share` on the
   <share-button> (snippets/share-button.liquid).

   Asked for 2026-08-24: one click copies the URL and says so, instead of opening
   a panel with a read-only field and a second button to press.

   ── What is reused rather than rebuilt ─────────────────────────────────────
   Nothing here invents markup or strings.

     · the URL comes from the vendor's own `<input>`, which Liquid already fills
       with the variant-aware `share_link`, and which `share.js` keeps up to date
       through its `updateUrl()` when the variant changes;
     · the notice is the vendor's `[id^="ShareMessage"]` span — it already carries
       `role="status"`, so screen readers announce it without any ARIA of ours.
       It is RELOCATED to be a direct child of <share-button>, because it lives
       inside the `<details>` panel that this behaviour stops opening. Same
       relocate-don't-duplicate pattern as header-drawer-localization.js;
     · the text is `window.accessibilityStrings.shareSuccess`, emitted from
       `layout/theme.liquid` via `| t` — so it is translated in all 52 locales and
       there is no user-facing literal in this file, which the house rules forbid.

   ── Degrade path ───────────────────────────────────────────────────────────
   The click is only intercepted when BOTH the Clipboard API and the translated
   string are available. If either is missing the listener is never attached and
   the vendor's `<details>` panel behaves exactly as before — a visitor is never
   left with a share control that silently does nothing.

   `navigator.share` is deliberately NOT used even where it exists. The native
   sheet is richer on mobile, but the request was explicit: click copies. Using
   one behaviour on phones and another on desktop would make the control mean two
   different things, which is worse than losing the sheet.
   ═══════════════════════════════════════════════════════════════════════════ */

(() => {
  'use strict';

  const NOTICE_MS = 2600;

  const init = (host) => {
    if (host.dataset.dcShareReady === 'true') return;

    const input = host.querySelector('input');
    const notice = host.querySelector('[id^="ShareMessage"]');
    const message = window.accessibilityStrings && window.accessibilityStrings.shareSuccess;

    if (!input || !notice || !message) return;
    if (!navigator.clipboard || !navigator.clipboard.writeText) return;

    host.dataset.dcShareReady = 'true';

    /* Out of the `<details>` panel, which no longer opens. */
    host.appendChild(notice);

    let timer = null;

    const announce = () => {
      notice.textContent = message;
      notice.classList.remove('hidden');
      notice.setAttribute('data-dc-share-notice', '');
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        notice.classList.add('hidden');
        notice.textContent = '';
      }, NOTICE_MS);
    };

    const copy = (event) => {
      event.preventDefault();
      /* Stops `share.js`'s own `toggle` handling from running as well — the
         summary would otherwise still flip the `<details>` open state even with
         the default prevented in some browsers. */
      event.stopPropagation();

      navigator.clipboard.writeText(input.value).then(announce).catch(() => {
        /* Clipboard can be refused at runtime (permissions, no user gesture in
           some embeddings). Fall back to what the theme did before by letting the
           panel open, rather than failing silently. */
        const details = host.querySelector('details');
        if (details) details.open = true;
      });
    };

    /* Both triggers: the `<summary>` is what renders when `navigator.share` is
       absent, the plain `<button>` is what `share.js` unhides when it is present.
       Capture phase so this runs before the vendor's own listeners. */
    host.querySelectorAll('summary, .share-button__button').forEach((trigger) => {
      trigger.addEventListener('click', copy, true);
    });
  };

  const initAll = () => {
    document.querySelectorAll('share-button[data-dc-share]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* The section re-renders through the Section Rendering API on a variant
     change, which replaces <share-button> and takes the ready flag with it. */
  new MutationObserver(initAll).observe(document.body, { childList: true, subtree: true });
})();
